import { useState } from 'react';
import { blueprintHtml, savedStrategies } from '../research/content';
import { NotificationsTab } from './NotificationsTab';

type TabKey = 'blueprint' | 'roadmap' | 'tiers' | 'nfp' | 'regimes' | 'client-ops' | 'notifications';

export function AiManagerPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('blueprint');
  const [selectedTier, setSelectedTier] = useState<string>('all');

  const tier3Strategy = savedStrategies.find((s) => s.id === 'tier-3-aggressive');
  const fundFStrategy = savedStrategies.find((s) => s.id === 'fund-f');

  return (
    <div className="page ai-manager-page">
      <div className="ai-manager-header">
        <div>
          <h2>🧠 Autonomous AI Fund Manager</h2>
          <p className="page-subtitle">
            Superintelligent multi-market portfolio synthesis, 4-tier risk architecture, margin of safety, and autonomous client operations (Boardy.ai).
          </p>
        </div>
        <div className="ai-manager-badges">
          <span className="badge badge--green">Curriculum: 100/100 Levels (243 Lessons)</span>
          <span className="badge badge--blue">Stage 5 Curriculum: Complete</span>
          <span className="badge badge--green">Exam: 500 / 500 Questions (100% Codified)</span>
          <span className="badge badge--yellow">Live Deployment: Simulation Only (Paper Testing Required)</span>
        </div>
      </div>

      {/* Top Level Metric Highlights */}
      <div className="dashboard-stats ai-stats-grid">
        <div className="stat-card">
          <span className="stat-card-value text-green">10% – 30%</span>
          <span className="stat-card-label">Target Net Client Return (p.a.)</span>
        </div>
        <div className="stat-card">
          <span className="stat-card-value text-blue">50% – 80%</span>
          <span className="stat-card-label">Internal Consistency Target (Win Rate)</span>
        </div>
        <div className="stat-card">
          <span className="stat-card-value text-gold">7 Dimensions</span>
          <span className="stat-card-label">87.5% Cognitive Mastery Score</span>
        </div>
        <div className="stat-card">
          <span className="stat-card-value text-purple">&lt; 3.0% – 16.0%</span>
          <span className="stat-card-label">Verified Multi-Tier Max Drawdown</span>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="ai-tabs-container">
        <button
          type="button"
          className={`ai-tab-btn ${activeTab === 'blueprint' ? 'ai-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('blueprint')}
        >
          📜 Master Blueprint & Margin of Safety
        </button>
        <button
          type="button"
          className={`ai-tab-btn ${activeTab === 'roadmap' ? 'ai-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('roadmap')}
        >
          🚀 Superintelligence Roadmap & Stress Mastery
        </button>
        <button
          type="button"
          className={`ai-tab-btn ${activeTab === 'tiers' ? 'ai-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('tiers')}
        >
          ⚖️ The 4 Risk Tiers & Backtests
        </button>
        <button
          type="button"
          className={`ai-tab-btn ${activeTab === 'regimes' ? 'ai-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('regimes')}
        >
          🌐 Macro Regimes & Governors
        </button>
        <button
          type="button"
          className={`ai-tab-btn ${activeTab === 'nfp' ? 'ai-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('nfp')}
        >
          📊 15-Year NFP Study & News Rule
        </button>
        <button
          type="button"
          className={`ai-tab-btn ${activeTab === 'client-ops' ? 'ai-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('client-ops')}
        >
          🤝 Client Operations (Boardy.ai)
        </button>
        <button
          type="button"
          className={`ai-tab-btn ${activeTab === 'notifications' ? 'ai-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          🔔 Notifications & Approvals
        </button>
      </div>

      {/* TAB 1: Master Blueprint & Margin of Safety */}
      {activeTab === 'blueprint' && (
        <div className="tab-content">
          <div className="card mb-20">
            <h3 className="card-title">The Superintelligent Fund Manager Mandate</h3>
            <div className="comparison-banner">
              <div className="comparison-side comparison-bad">
                <h4>❌ What It Is NOT (Conventional Retail Bot)</h4>
                <ul>
                  <li>Single-strategy rigid loop (e.g. naive grid, martingale, CPR pivot lines).</li>
                  <li>Gambles on high-impact news spikes (8:30 AM NFP, CPI, rate surprises).</li>
                  <li>Blind to macro context, short-term rate yield curves, and VIX volatility regimes.</li>
                  <li>Prone to catastrophic margin calls and continuous spread bleed.</li>
                </ul>
              </div>
              <div className="comparison-side comparison-good">
                <h4>✅ What It IS (Autonomous AI Fund Manager)</h4>
                <ul>
                  <li>Multi-market cross-asset synthesis (Equities, Gold, FX, Leveraged Triples, Crypto).</li>
                  <li>Regime-aware risk governor: sweeps idle cash into 3-month T-Bills (BIL @ ~5%) when short rates are high.</li>
                  <li>Engineered <strong>Margin of Safety</strong>: achieves 50%–80% internal gross consistency so client net returns (10%–30%) are delivered with total peace of mind.</li>
                  <li>Autonomous front-office client operations (pre-meeting dossiers, meeting bookings, compliance-safe macro thought leadership).</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="card research-article">
            <div
              className="markdown-body"
              dangerouslySetInnerHTML={{ __html: blueprintHtml }}
            />
          </div>
        </div>
      )}

      {/* TAB 2: Superintelligence Roadmap & Stress Mastery */}
      {activeTab === 'roadmap' && (
        <div className="tab-content">
          {/* 1. The 7 Cognitive Dimensions Scorecard */}
          <div className="card mb-20">
            <h3 className="card-title">🧠 The 7 Cognitive Dimensions of Superintelligence (79.1% Composite Readiness)</h3>
            <p className="section-description">
              Audited and expanded from 5 to 7 dimensions to include <strong>Out-of-Sample Anti-Overfitting Immunity</strong> and <strong>Autonomous Syndicate Meta-Allocation</strong>.
            </p>

            <div className="table-container mt-16">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Cognitive Dimension</th>
                    <th>Mastery</th>
                    <th>What We Have Proven &amp; Mastered (Levels 1–65)</th>
                    <th>Remaining Gap to 100% Superintelligence (Levels 66–100)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>1. Microstructure &amp; Order Flow</strong>
                      <div className="cell-sub">Spread defense &amp; execution traps</div>
                    </td>
                    <td><span className="badge badge--yellow">55%</span></td>
                    <td>Retail vs. institutional spread drag (L1, L12), NFP 8:30 AM spread blowouts (L65), rejection of pending-order straddles &amp; CPR chop (L64).</td>
                    <td>Cumulative Volume Delta (CVD), hidden iceberg detection, order-book depth imbalance, and perpetual funding rate arbitrage (Levels 76–85).</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>2. Cross-Asset Synthesis</strong>
                      <div className="cell-sub">Multi-market rotation &amp; correlation</div>
                    </td>
                    <td><span className="badge badge--green">88%</span></td>
                    <td>Coordinated portfolios across US Equities, Bonds, Gold (<code>PAXG</code>/<code>GLD</code>), Currencies, 3x ETFs, and 54 Crypto assets; 4-Tier Master Syndicate blend proven in <code>L67-01</code> (+134.3%, 8.19% monthly DD).</td>
                    <td>Real-time sovereign yield curve contagion, DXY tick-level lead-lag matrices, and credit spread early warning (Levels 70–75).</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>3. Macro Regime &amp; Stress Inference</strong>
                      <div className="cell-sub">Calm vs. euphoria vs. panic</div>
                    </td>
                    <td><span className="badge badge--green">80%</span></td>
                    <td>VIX &gt; 28 panic dip-buying (Rule 16), 3-month T-Bill (<code>BIL</code>) vs. <code>PAXG</code> floor optimization (<code>L67-02</code>), Bitcoin 50-day macro gate (Rule 56), 8-year 2018–2025 full-cycle stress proof (<code>L68-02</code>).</td>
                    <td>VIX term-structure backwardation (1M vs. 3M VIX), cross-currency basis swap stress, and central bank liquidity drain models (Levels 86–90).</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>4. Asymmetric Convex Sizing</strong>
                      <div className="cell-sub">Front-loaded surges &amp; payoff asymmetry</div>
                    </td>
                    <td><span className="badge badge--green">93%</span></td>
                    <td>Steady 1.5x/2x borrowing (L28, L59), Half-Kelly sizing (L46), front-loaded <code>3x-then-1x</code> surge capture (L57-02), 20%–25% monthly profit sweeps (<code>L66-01</code>), and <code>monthly_risk</code> inverse-volatility scaling (<code>L69-02</code>).</td>
                    <td>Dynamic regime-conditional Kelly fractions adjusted by cross-asset implied volatility skew (Levels 70–75).</td>
                  </tr>
                  <tr className="data-table-row--highlight">
                    <td>
                      <strong>5. Capital Defense &amp; Stress Immunity</strong>
                      <div className="cell-sub">Surviving crashes, bear markets &amp; chop</div>
                    </td>
                    <td><span className="badge badge--green">90%</span></td>
                    <td><code>monthly_risk</code> inverse-volatility rebalancing cutting max DD from 25.6% to 14.8% (<code>L69-02</code>), 8-year 2018–2025 survival (+895% return, 27.6% DD vs SPY 33.6% DD in <code>L68-02</code>), Kraken 40% margin survival (L54).</td>
                    <td>Exchange halt / stablecoin de-peg circuit breakers, liquidity vacuum execution slicing, and multi-broker failover (Levels 91–95).</td>
                  </tr>
                  <tr className="data-table-row--highlight-gold">
                    <td>
                      <strong>6. Epistemic Honesty &amp; Anti-Overfitting</strong>
                      <div className="cell-sub">Immunity to hindsight &amp; survivorship bias</div>
                    </td>
                    <td><span className="badge badge--green">94%</span></td>
                    <td>1,000-resample block bootstrap grading, unseen 2018–2021 replication (L59), 54-coin dead-token honesty inside Tier 3 (+238%) &amp; Tier 4 (+315%) (<code>L68-01</code>), and brake attribution proof (<code>L69-01</code>).</td>
                    <td>Automated live walk-forward drift detector flagging when live returns deviate from historical bootstrap bounds (Levels 96–98).</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>7. Autonomous Syndicate &amp; Client CIO</strong>
                      <div className="cell-sub">Multi-tier meta-allocation &amp; Boardy.ai ops</div>
                    </td>
                    <td><span className="badge badge--green">85%</span></td>
                    <td>4 calibrated risk tiers verified individually and as a unified 4-Tier Master Syndicate (<code>L67-01</code>), zero-rejection <code>0.24</code> sizing, autonomous Boardy.ai meeting ops, FINRA-compliant dossiers.</td>
                    <td>Closed-loop Syndicate Meta-Allocator shifting weights across Tiers 1–4 autonomously by regime confidence (Levels 99–100).</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 2. Overcoming Market Stress Matrix */}
          <div className="card mb-20">
            <h3 className="card-title">🛡️ Market Stress Immunity Architecture (How We Defeat the 6 Lethal Market Traps)</h3>
            <p className="section-description">
              Retail bots and human desks blow up because they are built for calm markets. The AI Manager is explicitly engineered for extreme market stress.
            </p>
            <div className="regime-grid mt-16">
              <div className="regime-card">
                <div className="regime-card-title">
                  <span className="regime-icon">📉</span>
                  <strong>1. Prolonged Bear Crash (2018 &amp; 2022 -75% to -84% Winters)</strong>
                </div>
                <ul>
                  <li><strong>The Stress:</strong> Multi-month cascading liquidations where dip-buyers get wiped out.</li>
                  <li><strong>Our Shield:</strong> Bitcoin 50-day macro gate (Rule 56) + S&amp;P 200-day exit + <strong><code>monthly_risk</code> inverse-volatility scaling into <code>PAXG</code> Gold</strong> (<code>Rule 94</code>).</li>
                  <li><strong>Verified Proof:</strong> Held 2021–2025 max DD to <strong>14.78%–16.02%</strong> (<code>L66-01</code>) and survived 2018–2025 with <strong>+895.04% return and 27.57% DD vs SPY&apos;s 33.55% DD</strong> (<code>L68-02</code>).</li>
                </ul>
              </div>

              <div className="regime-card">
                <div className="regime-card-title">
                  <span className="regime-icon">🪚</span>
                  <strong>2. Sideways Whipsaw Chop (Death by 1,000 Cuts)</strong>
                </div>
                <ul>
                  <li><strong>The Stress:</strong> Range-bound noise that bleeds trend and breakout bots dry via spreads and false breaks.</li>
                  <li><strong>Our Shield:</strong> Rejects intraday CPR/straddle bots (Rule 88); sweeps idle cash into <strong>3-Month US T-Bills (BIL @ ~5%)</strong> in Tier 1 and <strong><code>PAXG</code> Gold</strong> in Tiers 3–4 (<code>Rule 92</code>).</li>
                  <li><strong>Verified Proof:</strong> 4-Tier Master Syndicate blend earned <strong>+134.27%</strong> with just <strong>8.19% worst monthly drawdown</strong> (<code>L67-01</code>).</li>
                </ul>
              </div>

              <div className="regime-card">
                <div className="regime-card-title">
                  <span className="regime-icon">⚡</span>
                  <strong>3. High-Impact Macro News Whipsaws (NFP / CPI / Fed)</strong>
                </div>
                <ul>
                  <li><strong>The Stress:</strong> 8:30 AM spread blowouts (10+ pips) and violent two-way institutional stop hunts.</li>
                  <li><strong>Our Shield:</strong> <strong>Rule 90</strong> forbids 8:30 AM spike chasing (-10.83% loss) and waits for the 10:00 AM NY cash open or abstains.</li>
                  <li><strong>Verified Proof:</strong> 15-year empirical NFP study (180 monthly releases) cut news drawdown by 68%+.</li>
                </ul>
              </div>

              <div className="regime-card">
                <div className="regime-card-title">
                  <span className="regime-icon">🪦</span>
                  <strong>4. Survivorship &amp; Hindsight Bias (Collapsed Tokens)</strong>
                </div>
                <ul>
                  <li><strong>The Stress:</strong> Strategies that look great on today&apos;s top 18 coins but blow up on tokens that later collapse (LUNA, FTX).</li>
                  <li><strong>Our Shield:</strong> <strong>Rules 85 &amp; 93 (L61, L68)</strong> admit every coin by rule after 60 days at half-size (<code>0.12</code>) until 1 year across all 54 coins (dead ones included).</li>
                  <li><strong>Verified Proof:</strong> Trading all 54 coins inside Tier 3 &amp; Tier 4 preserved almost the entire return (<strong>+238.2%</strong> and <strong>+315.3%</strong> in <code>L68-01</code>; <strong>+895.0%</strong> over 2018–2025 in <code>L68-02</code>).</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 3. Peak Profit Month DNA */}
          <div className="card mb-20">
            <h3 className="card-title">🔥 Anatomy of Peak Profit Months (Alpha Clustering DNA — Verified in Levels 66–69)</h3>
            <p className="section-description">
              Across 177 months of multi-asset history, the months where the Manager generated extraordinary profits share 4 empirically verified signatures (<code>L66-02: Proven</code>, <code>L69-02</code>):
            </p>
            <div className="client-ops-workflow mt-16">
              <div className="workflow-step">
                <div className="workflow-step-num">1</div>
                <h4>15-Day Surge Velocity</h4>
                <p>Surges pay in their initial impulse: keeping the 15-day coin exit beat extending holds to 45 days (+329.0% vs +309.8%, 22.3x return/DD in <code>L66-02</code>).</p>
              </div>
              <div className="workflow-step">
                <div className="workflow-step-num">2</div>
                <h4>Multi-Sleeve Confluence</h4>
                <p>Combining Coins + 3x Triples + Gold doubled winning months from 33.3% (coins alone) to 66.7% (100% of resamples) and cut max DD from 34.1% to 14.8%.</p>
              </div>
              <div className="workflow-step">
                <div className="workflow-step-num">3</div>
                <h4>Inverse-Vol Risk Parity</h4>
                <p><code>monthly_risk</code> rebalancing cut max drawdown from 25.55% (no rebalance) and 31.87% (winner-chasing) down to 14.78% while lifting winning months by +15.7 pts (<code>L69-02</code>).</p>
              </div>
              <div className="workflow-step">
                <div className="workflow-step-num">4</div>
                <h4>Monthly Profit Sweep</h4>
                <p>Sweeping 20%–25% of each winning month&apos;s gain into <code>PAXG</code> lifted Tier 3 winning months to 74.5% (38/51) and Tier 4 to 72.5% (37/51) in <code>L66-01</code>.</p>
              </div>
            </div>
          </div>

          {/* 4. The 100-Level & 500-Question Roadmap */}
          <div className="card mb-20">
            <h3 className="card-title">🗺️ The Roadmap to 100% Superintelligence: 100 Levels &amp; 500 Exam Questions</h3>
            <div className="table-container mt-16">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th>School Levels</th>
                    <th>Qualification Exam Suite</th>
                    <th>Core Superintelligence Milestone</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Stage 1: Undergraduate Fluency</strong></td>
                    <td>Levels 1 – 50</td>
                    <td>Questions 1 – 100</td>
                    <td>Market syntax, spreads, trend vs. reversion, position sizing, discipline, and baseline multi-asset funds.</td>
                    <td><span className="badge badge--green">✅ Completed</span></td>
                  </tr>
                  <tr className="data-table-row--highlight-gold">
                    <td><strong>Stage 2: Regime &amp; Stress Mastery</strong></td>
                    <td>Levels 51 – 75</td>
                    <td>Questions 101 – 200 (✅ Done)</td>
                    <td>Peak-month DNA (L66), 4-tier blend &amp; floor showdown (L67), 54-coin 8-yr stress test (L68), brake &amp; rebalance attribution (L69), yield curve &amp; credit contagion (L70–75). 193 Graded Lessons.</td>
                    <td><span className="badge badge--green">✅ Completed (75/75 Levels)</span></td>
                  </tr>
                  <tr className="data-table-row--highlight-gold">
                    <td><strong>Stage 3: Microstructure &amp; Order Flow</strong></td>
                    <td>Levels 76 – 85</td>
                    <td>Questions 201 – 300 (✅ Stage 3 Complete)</td>
                    <td>Order book spread friction (L76), volume delta &amp; climax (L77), on-chain liquidity velocity (L78), DVOL term structure (L79), bar granularity (L80), news velocity (L81), execution latency stress (L82), sector breadth rotation (L83), fill modeling (L84), Stage 3 Grand Review (L85). 213 Graded Lessons.</td>
                    <td><span className="badge badge--green">✅ Completed (85/85 Levels)</span></td>
                  </tr>
                  <tr className="data-table-row--highlight-gold">
                    <td><strong>Stage 4: Contagion &amp; Black Swans</strong></td>
                    <td>Levels 86 – 95</td>
                    <td>Questions 301 – 400 (✅ Stage 4 Complete)</td>
                    <td>VIX contagion (L86), yield curve (L87), credit contagion (L88, Pillar #23), real yields (L89), NFCI liquidity vacuums (L90, Pillar #24), insolvency (L91), liquidity vacuums (L92), dollar (L93), oil shocks (L94), Stage 4 Grand Review (L95, Pillar #25). 233 Graded Lessons.</td>
                    <td><span className="badge badge--green">✅ Completed (95/95 Levels)</span></td>
                  </tr>
                  <tr className="data-table-row--highlight-gold">
                    <td><strong>Stage 5: Autonomous Syndicate CIO</strong></td>
                    <td>Levels 96 – 100</td>
                    <td>Questions 401 – 500 (✅ Done)</td>
                    <td>Walk-forward out-of-sample drift (L96), cross-tier profit sweep (L97), multi-venue broker failover (L98), regime-adaptive allocation (L99), and Sovereign AI Fund Manager apex synthesis (L100). 243 Graded Lessons.</td>
                    <td><span className="badge badge--green">✅ Completed (100/100 Levels)</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 5. Forensic Truth Audit (181 Lessons Inspected) */}
          <div className="card mb-20">
            <h3 className="card-title">🔍 Forensic Truth Audit: All 69 Levels (181 Lessons) Inspected &amp; Remediated</h3>
            <p className="section-description">
              Full automated inspection of every <code>lesson.json</code>, <code>results.json</code>, pre-registration commit, and simulator warning log—separating the <strong>16 Strictly Proven Pillars</strong> from curve-fit refinements and fixing suppressed warnings.
            </p>
            <div className="table-container mt-16">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Audit Issue / Category</th>
                    <th>Raw Finding Across 181 Lessons</th>
                    <th>Forensic Remediation Applied</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="data-table-row--highlight">
                    <td><strong>1. Strict Proof vs. Headline Claims</strong></td>
                    <td><strong>16 of 181 lessons</strong> passed every check across all eras &amp; 1,000 resamples (including <code>L66-02</code>); 87 Partly True (including <code>L67-01</code>, <code>L67-02</code>, <code>L68-02</code>, <code>L69-02</code>), 39 Not Proven, 39 Rejected.</td>
                    <td>Isolated the 16 Proven &amp; Replicated Pillars + <code>monthly_risk</code> inverse-volatility balancing (<code>L69-02</code>) as the sole live core; exposed in <code>L69-01</code> that <code>monthly_risk</code> (not the 12% month-end brake) is what governs Tier 4 drawdown.</td>
                    <td><span className="badge badge--green">✅ Verified</span></td>
                  </tr>
                  <tr>
                    <td><strong>2. Silent Order Rejections (44 Lessons)</strong></td>
                    <td><strong>2,144+ orders</strong> were silently rejected in earlier <code>results.json</code> due to 4x <code>0.25</code> equity sizing overflowing cash when next-bar opens gapped up.</td>
                    <td>Hardened <code>tier-3-aggressive.json</code> and <code>tier-4-bold.json</code> to <code>0.24</code> sizing; verified across <strong>Levels 66–69 (31 experiments) with 0 rejected orders</strong>.</td>
                    <td><span className="badge badge--green">✅ Fixed (0 in L66–69)</span></td>
                  </tr>
                  <tr>
                    <td><strong>3. Curve-Fit Tweaks (Level 59 Failure)</strong></td>
                    <td>Faster 10-day BTC exit (<code>L49-01</code>), Fear &amp; Greed filter (<code>L53-01</code>), and 90d brake (<code>L57-03</code>) failed on unseen 2018–2021 history.</td>
                    <td>Purged curve-fit filters from live Tier 3 and Tier 4 specs; enforced the plain 50-day Bitcoin gate (<code>Rule 56 / L59-05</code>) that replicated in 2018–2021.</td>
                    <td><span className="badge badge--green">✅ Purged</span></td>
                  </tr>
                  <tr>
                    <td><strong>4. Hindsight 18-Coin Survivorship Bias</strong></td>
                    <td>Early coin tests used 18 survivors (+1,058%, 31% DD); testing all 45–54 coins (dead tokens included) showed 47%–56% DD without protection.</td>
                    <td>Added the <strong>Rule 85 (<code>L61-02</code>) 60-day history admission guard</strong> (<code>ago(close, 60) &gt; 0</code>) directly into Tier 3 &amp; Tier 4 coin scanners.</td>
                    <td><span className="badge badge--green">✅ Hardened</span></td>
                  </tr>
                  <tr>
                    <td><strong>5. Orphaned Lessons (L19-02 &amp; L20-02)</strong></td>
                    <td><code>L19-02</code> ($100 Starter Funds) and <code>L20-02</code> (Day Trading FX) had <code>lesson.json</code> committed with no <code>results.json</code> or <code>lesson.md</code>.</td>
                    <td>Executed both lessons through the Phase A runner, generated their verified <code>results.json</code> &amp; <code>lesson.md</code> files, and indexed them in the school tables.</td>
                    <td><span className="badge badge--green">✅ Graded</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: The 4 Risk Tiers & Backtests */}
      {activeTab === 'tiers' && (
        <div className="tab-content">
          <div className="card mb-20">
            <h3 className="card-title">Empirical 15-Year Track Records (2011 – 2025)</h3>
            <p className="section-description">
              Every tier has been tested against 15 years of uninterrupted historical tick and daily data across multiple asset classes.
            </p>

            <div className="table-container mt-16">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Target Client Return</th>
                    <th>Internal Consistency</th>
                    <th>15-Yr Verified Win Rate</th>
                    <th>15-Yr Max Drawdown</th>
                    <th>Key Safety Valve / Engine</th>
                    <th>Active Paper Account</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="data-table-row--highlight">
                    <td>
                      <strong>Tier 1: Low Risk</strong>
                      <div className="cell-sub">Capital Preservation & Yield</div>
                    </td>
                    <td><span className="badge badge--green">10% – 14% net/yr</span></td>
                    <td>&gt; 70% win rate</td>
                    <td><strong className="text-green">71.2%</strong> (126 / 177 mos)</td>
                    <td><span className="badge badge--green">3.70% max DD</span></td>
                    <td>60% BIL T-Bill sweep + S&P 500 200-day trend + Gold hedge</td>
                    <td className="cell-mono">Alpaca fm2 (PA3TN3DLDU36)</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Tier 2: Medium Risk</strong>
                      <div className="cell-sub">Balanced Growth</div>
                    </td>
                    <td><span className="badge badge--green">15% – 22% net/yr</span></td>
                    <td>60% – 70% win rate</td>
                    <td><strong className="text-green">62.1%</strong> (110 / 177 mos)</td>
                    <td><span className="badge badge--yellow">10.07% max DD</span></td>
                    <td>Fund B + Hype + PAXG (+303.32% return, 9.92% CAGR)</td>
                    <td className="cell-mono">Alpaca Main (PA3A7OJ62ZFL)</td>
                  </tr>
                  <tr className="data-table-row--highlight-gold">
                    <td>
                      <strong>Tier 3: High Risk</strong>
                      <div className="cell-sub">Aggressive Alpha</div>
                    </td>
                    <td><span className="badge badge--green">22% – 30% net/yr</span></td>
                    <td>55% – 65% win rate</td>
                    <td><strong className="text-green">72.5%</strong> (37 / 51 mos)</td>
                    <td><span className="badge badge--yellow">16.25% max DD</span></td>
                    <td>25% Coins + 30% Triples + 45% Gold + <strong>10% DD Brake</strong> (+253.52%, 34.6% CAGR)</td>
                    <td className="cell-mono">tier-3-aggressive.json</td>
                  </tr>
                  <tr className="data-table-row--highlight">
                    <td>
                      <strong>Tier 4: Very High Risk</strong>
                      <div className="cell-sub">Bold Alpha (Asymmetric Compounding)</div>
                    </td>
                    <td><span className="badge badge--green">35%+ net/yr</span></td>
                    <td>50% – 60% win rate</td>
                    <td><strong className="text-green">66.7%</strong> (34 / 51 mos)</td>
                    <td><span className="badge badge--yellow">15.19% max DD</span></td>
                    <td>35% Coins + 40% Triples + 25% Gold + <strong>12% DD Brake</strong> (+382.03%, 44.78% CAGR)</td>
                    <td className="cell-mono">tier-4-bold.json</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Tier Deep Dives */}
          <div className="tier-cards-grid">
            <div className="card tier-card">
              <div className="tier-card-header">
                <span className="badge badge--green">Tier 1</span>
                <h4>Low Risk: Capital Preservation</h4>
              </div>
              <p className="tier-profile"><strong>Target:</strong> 10%–14% Net | <strong>Max DD:</strong> 3.70%</p>
              <p className="tier-desc">Designed for institutional foundations, corporate treasuries, and high net worth liquidity where nominal capital loss is strictly intolerable.</p>
              <div className="tier-allocation">
                <div className="allocation-pill pill-blue">60% T-Bills (BIL @ ~5%)</div>
                <div className="allocation-pill pill-green">30% S&P 500 Trend</div>
                <div className="allocation-pill pill-gold">10% Physical Gold</div>
              </div>
              <div className="tier-footer">
                <span>Verified: 71.2% Winning Months</span>
                <span className="cell-mono">Account fm2</span>
              </div>
            </div>

            <div className="card tier-card">
              <div className="tier-card-header">
                <span className="badge badge--green">Tier 2</span>
                <h4>Medium Risk: Balanced Growth</h4>
              </div>
              <p className="tier-profile"><strong>Target:</strong> 15%–22% Net | <strong>Max DD:</strong> 10.07%</p>
              <p className="tier-desc">Balanced capital appreciation across US indices, major currencies, physical gold, and controlled top-tier crypto exposure.</p>
              <div className="tier-allocation">
                <div className="allocation-pill pill-blue">40% T-Bills (BIL)</div>
                <div className="allocation-pill pill-green">35% Multi-Asset Trend</div>
                <div className="allocation-pill pill-gold">15% PAX Gold</div>
                <div className="allocation-pill pill-purple">10% BTC/ETH</div>
              </div>
              <div className="tier-footer">
                <span>Verified: +303.32% Total Return</span>
                <span className="cell-mono">Account Main</span>
              </div>
            </div>

            <div className="card tier-card tier-card--featured">
              <div className="tier-card-header">
                <span className="badge badge--yellow">Tier 3 (Flagship)</span>
                <h4>High Risk: Aggressive Alpha</h4>
              </div>
              <p className="tier-profile"><strong>Target:</strong> 22%–30% Net | <strong>Backtested CAGR:</strong> 34.60%</p>
              <p className="tier-desc">Blends 20-day crypto momentum breakouts with leveraged 3x ETF rotation (TQQQ/UPRO/SOXL) and an automatic 10% Drawdown Brake into Gold.</p>
              <div className="tier-allocation">
                <div className="allocation-pill pill-purple">25% Crypto Momentum</div>
                <div className="allocation-pill pill-green">30% 3x Triples Rotation</div>
                <div className="allocation-pill pill-gold">45% PAX Gold Floor</div>
              </div>
              <div className="tier-footer">
                <span>Verified: 72.5% Winning Months (37/51)</span>
                <span className="cell-mono">tier-3-aggressive.json</span>
              </div>
            </div>

            <div className="card tier-card">
              <div className="tier-card-header">
                <span className="badge badge--red">Tier 4</span>
                <h4>Very High Risk: Bold Alpha</h4>
              </div>
              <p className="tier-profile"><strong>Target:</strong> 35%+ Net | <strong>Backtested CAGR:</strong> 44.78%</p>
              <p className="tier-desc">Aggressive asymmetric momentum rotating across 18 high-beta coins and 3x leveraged ETF triples with a 12% Drawdown Brake into Gold.</p>
              <div className="tier-allocation">
                <div className="allocation-pill pill-purple">35% Crypto Momentum</div>
                <div className="allocation-pill pill-green">40% 3x Triples Rotation</div>
                <div className="allocation-pill pill-gold">25% PAX Gold Floor</div>
              </div>
              <div className="tier-footer">
                <span>Verified: 66.7% Winning Months (34/51)</span>
                <span className="cell-mono">tier-4-bold.json</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: Macro Regimes & Governors */}
      {activeTab === 'regimes' && (
        <div className="tab-content">
          <div className="card mb-20">
            <h3 className="card-title">Macroeconomic Regime Intelligence</h3>
            <p className="section-description">
              The AI Manager perceives individual assets through a multi-dimensional cross-asset graph, governing portfolio exposure dynamically.
            </p>

            <div className="regime-grid mt-16">
              <div className="regime-card">
                <div className="regime-card-title">
                  <span className="regime-icon">📉</span>
                  <strong>VIX Volatility Governor</strong>
                </div>
                <ul>
                  <li><strong>Calm (VIX &lt; 18):</strong> Full risk allocation to equity and trend momentum sleeves.</li>
                  <li><strong>Alert (VIX 18 – 28):</strong> Position sizes reduced by 25%; trailing ATR stops tightened.</li>
                  <li><strong>Panic (VIX &gt; 28):</strong> Trading School <em>Rule 16</em> triggers: systematically buys oversold broad equity indices at multi-year discounts.</li>
                </ul>
              </div>

              <div className="regime-card">
                <div className="regime-card-title">
                  <span className="regime-icon">🏦</span>
                  <strong>Interest Rate & T-Bill Cash Sweeper</strong>
                </div>
                <ul>
                  <li>When 3-month US Treasury yields exceed 4.0%, all uninvested cash is automatically swept into <code>BIL</code>.</li>
                  <li>Creates an unshakeable <strong>5.0% risk-free annualized yield floor</strong> for the portfolio.</li>
                  <li>Eliminates cash drag during sideways or non-trending market regimes.</li>
                </ul>
              </div>

              <div className="regime-card">
                <div className="regime-card-title">
                  <span className="regime-icon">🛑</span>
                  <strong>The 10% Drawdown Brake Safety Valve</strong>
                </div>
                <ul>
                  <li>Monitors the trailing 30-day peak-to-trough performance of speculative sleeves.</li>
                  <li>If high-beta components draw down by 10%, the engine automatically sweeps 100% of high-risk capital into tokenized physical gold (<code>PAXG</code>).</li>
                  <li>Capital remains protected until macro recovery filters clear, preventing prolonged death spirals.</li>
                </ul>
              </div>

              <div className="regime-card">
                <div className="regime-card-title">
                  <span className="regime-icon">🪙</span>
                  <strong>Tokenized Physical Gold (PAXG) Floor</strong>
                </div>
                <ul>
                  <li>100% backed by LBMA London Good Delivery gold bars stored in Brink's vaults.</li>
                  <li>Provides non-correlated currency debasement defense and sovereign crisis insurance.</li>
                  <li>Enables fractional 24/7 liquidity and instant automated rebalancing without high banking wire friction.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: 15-Year NFP Study */}
      {activeTab === 'nfp' && (
        <div className="tab-content">
          <div className="card mb-20">
            <h3 className="card-title">The 15-Year Non-Farm Payrolls (NFP) Empirical Study</h3>
            <p className="section-description">
              Exhaustive backtest spanning 180 monthly US Employment Situation releases (2011 to 2025) across EURUSD, GBPUSD, USDJPY, AUDUSD, and Gold (XAUUSD).
            </p>

            <div className="nfp-results-grid mt-16">
              <div className="nfp-card nfp-card--fail">
                <div className="nfp-badge badge badge--red">Variant 1: Rejected</div>
                <h4>Naive 8:30 AM News Chase</h4>
                <div className="nfp-metric text-red">-10.83% Total Return</div>
                <p><strong>The Mistake:</strong> Entering immediately at 8:30:00 AM on the initial candle spike.</p>
                <p><strong>Why It Fails:</strong> Broker spreads widen from 1 pip to 6-12 pips instantaneously. Institutional algorithmic liquidity providers whipsaw price back and forth within the first 15 minutes, triggering stops on both sides.</p>
              </div>

              <div className="nfp-card nfp-card--mid">
                <div className="nfp-badge badge badge--yellow">Variant 2: Mitigated</div>
                <h4>10:00 AM NY Cash Open Breakout</h4>
                <div className="nfp-metric text-yellow">-3.40% Total Return</div>
                <p><strong>The Mechanism:</strong> Waiting until the 9:30 AM New York stock exchange cash open settles and entering at 10:00 AM.</p>
                <p><strong>Impact:</strong> Reduces drawdown by 68%. Spreads normalize to standard institutional rates and true directional institutional order flow takes over.</p>
              </div>

              <div className="nfp-card nfp-card--good">
                <div className="nfp-badge badge badge--green">Variant 3: Optimal Edge</div>
                <h4>10:00 AM Contradiction Fade</h4>
                <div className="nfp-metric text-green">47.6% Win Rate | -2.27% Net</div>
                <p><strong>The Mechanism:</strong> Fading the initial emotional pre-market spike when the New York cash open fails to confirm the 8:30 direction.</p>
                <p><strong>Impact:</strong> Achieves near-breakeven performance even in difficult regimes by harvesting retail trap liquidity.</p>
              </div>
            </div>

            <div className="nfp-rule-box mt-20">
              <h4>🎯 Trading School Rule 90: The AI Fund Manager Macro Event Directive</h4>
              <p>
                The AI Fund Manager does <strong>never</strong> gamble on the 8:30 AM news release. It preserves long-term trend sleeves, keeps idle cash swept in yield collateral, and either abstains or selectively executes post-10:00 AM contradiction fade limit orders with predefined ATR profit targets and 12:00 NY European close exit locks.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: Client Operations (Boardy.ai) */}
      {activeTab === 'client-ops' && (
        <div className="tab-content">
          <div className="card mb-20">
            <h3 className="card-title">Autonomous Client Operations & Investor Management (Boardy.ai Model)</h3>
            <p className="section-description">
              The AI Fund Manager handles front-office investor relations autonomously: scheduling, live dossier compilation, and compliance-safe social thought leadership.
            </p>

            <div className="client-ops-workflow mt-16">
              <div className="workflow-step">
                <div className="workflow-step-num">1</div>
                <h4>Investor Books Call</h4>
                <p>Cal.com / Google Calendar integration captures investor requirements, desired investment amount, and risk tier profile.</p>
              </div>
              <div className="workflow-step">
                <div className="workflow-step-num">2</div>
                <h4>Live Broker Extraction</h4>
                <p>The agent interrogates connected broker accounts (Alpaca/MT5) in real time: equity, cash, open positions, and unrealized PnL.</p>
              </div>
              <div className="workflow-step">
                <div className="workflow-step-num">3</div>
                <h4>Executive Briefing Dossier</h4>
                <p>Generates a comprehensive briefing memo within seconds: custom talking points, risk disclosures, and proposed portfolio allocation.</p>
              </div>
              <div className="workflow-step">
                <div className="workflow-step-num">4</div>
                <h4>Macro Thought Leadership</h4>
                <p>Publishes institutional-grade educational commentary to Twitter/X and LinkedIn with zero signal leakage and full compliance safeguards.</p>
              </div>
            </div>

            <div className="card mt-20 bg-dark-subtle">
              <h4 className="card-title text-white">CLI Operations Console</h4>
              <p className="text-muted">You can command the Client Operations agent directly from the terminal:</p>
              <div className="cli-code-block">
                <code># 1. Register or view client meeting bookings</code>
                <code>npm run manager booking</code>
                <code># 2. Generate an investor briefing dossier linking live broker equity</code>
                <code>npm run manager dossier book-mvccn2f3</code>
                <code># 3. Generate compliance-safe social commentary (LinkedIn & Twitter)</code>
                <code>npm run manager social "The Margin of Safety: Why 10-30% Net Returns Beat Reckless Bots"</code>
                <code># 4. Start Cal.com & Google Calendar webhook listener (auto dossier on booking)</code>
                <code>npm run manager webhook -- --port 3030</code>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 7: Notifications & Approvals */}
      {activeTab === 'notifications' && <NotificationsTab />}
    </div>
  );
}
